import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { homeSectionsToDraw, lineSummary, sizeTitle, wallClock, type OrderDto } from '@tikkas/shared';
import { ART, COIN, IC, Svg, imageFor } from '../art/icons.js';
import { fmt, hhmmLocal, readyShort } from '../lib/format.js';
import { useNav } from '../lib/router.js';
import { orderingOpen, rewardPoints, useStore, welcomeToCome } from '../lib/store.js';
import { OrderingClosed, Scroll } from '../components/ui.js';
import { newBuilder, safePrice } from '../lib/bag.js';
import { track } from '../lib/events.js';
import { MessageCard } from './Messages.js';
import { apiUrl, testToolsAllowed } from '../lib/platform.js';
import { openLink } from '../lib/open.js';
import { linkTarget } from '../lib/links.js';
import { logoUrlFor, useLook } from '../lib/look.js';
import { AccountButton } from '../components/TabBar.js';
import { accountIsTab } from '../lib/appearance.js';
import { wantsCode, withoutCode } from '../lib/cardLinks.js';
import { DEFAULT_HOME, keptHome, loadHome, type HomeBlockId, type HomeDto, type WhatsNewCard } from '../lib/home.js';
import { TikkasCard, useCard, cardFace, CardLayers, CardLogo } from '../components/TikkasCard.js';
import { useJustHappened } from '../lib/justHappened.js';
import { welcomeLine } from '../lib/joining.js';
import { guestOrderPath } from '../lib/guest.js';
import { useNotificationsAsk } from '../components/NotificationsAsk.js';

/*
 * Home (CLAUDE.md §4, Saif 21–22 Oct 2026): the sections the admin ordered (shared appearance/home.ts): the greeting as
 * the page title, the Tikkas card with the balance as the focus (its three-dot menu opens Activity,
 * Wallet and how points work), the "just happened" line, the Top up · Rewards · Earn pills, the live order OR the usual,
 * What's new, and the big Today block when it's switched back on. The floating amber Show code lives here. Signed out: the
 * join card in the card's place, Order now, the status line and What's new.
 */

export function grillPct(o: OrderDto): number {
  const mins = Math.max(0, (new Date(o.readyAt).getTime() - Date.now()) / 60000);
  const p = Math.round(100 * (1 - Math.min(12, mins) / 12));
  return o.status === 'placed' ? Math.max(10, Math.min(40, p)) : Math.max(45, Math.min(92, p));
}

/** One live order. A guest's (docs/30) opens by its link, and its Show code opens the order's own code pop-up. */
export function LiveRow({ o, guestToken }: { o: OrderDto; guestToken?: string | undefined }) {
  const s = useStore();
  const { navigate } = useNav();
  const href = guestToken ? guestOrderPath(o.id, guestToken) : `/order/${o.id}`;
  const menu = s.menuData?.menu;
  const tk = o.lines.find((l) => menu?.items[l.itemId]?.hasTikkas && l.config.portions.length);
  const item = tk ? menu?.items[tk.itemId] : undefined;
  const nm = menu && tk ? sizeTitle(menu, tk.config) : `Order #${o.number}`;
  const art = tk && item ? <img src={imageFor(item.image, item.photoUrl)} alt="" /> : null;
  if (o.status === 'ready') {
    return (
      <div className="live2 ready">
        <span className="lthumbm">{art ?? <Svg html={IC.bell} />}</span>
        <button type="button" className="grow col lcol" onClick={() => navigate(href)}><b>Ready to collect</b><span>#{o.number} · {nm}</span></button>
        <button type="button" className="lcode" onClick={() => (guestToken ? navigate(guestOrderPath(o.id, guestToken, '&code=1')) : s.openSheet({ kind: 'code' }))}><Svg html={IC.qr} /> Show code</button>
      </div>
    );
  }
  return (
    <button type="button" className="live2" onClick={() => navigate(href)}>
      <span className="lthumbm cooking">{art ?? <span className="dot" />}</span>
      <span className="grow col lcol"><b>#{o.number} · {o.status === 'preparing' ? 'On the grill' : 'Order confirmed'}</b><span>{nm} · ready {readyShort(o.readyAt)}</span><span className="grill sm"><i style={{ width: `${grillPct(o)}%` }} /></span></span>
      <Svg html={IC.chev} />
    </button>
  );
}

/** Today at Tikkas: open now with the ready time, or when the next collection is. */
function todayLine(s: ReturnType<typeof useStore>, say: (k: 'today.open' | 'today.closed') => string): { open: boolean; head: string; sub: string } {
  const days = s.pickupDays;
  if (!days.length) return { open: false, head: 'Today at Tikkas', sub: '1 Great Titchfield St, by Oxford Circus' };
  const hours = s.config?.openingHours?.[wallClock(new Date()).weekday];
  const asap = days[0]?.asapReadyAt;
  if (asap) {
    const mins = Math.max(1, Math.round((new Date(asap).getTime() - Date.now()) / 60000));
    return { open: true, head: say('today.open'), sub: `Ready in about ${mins} min${hours ? ` · open until ${hours.close}` : ''}` };
  }
  const d = days.find((x) => x.slots.length);
  if (!d) return { open: false, head: say('today.closed'), sub: 'Back soon' };
  const t = hhmmLocal(d.slots[0]!);
  return { open: false, head: say('today.closed'), sub: `Order ahead · ready from ${d.label === 'Today' ? `today at ${t}` : d.label === 'Tomorrow' ? `tomorrow at ${t}` : `${d.label} at ${t}`}` };
}

/** The big Today at Tikkas block (a section switch, off at the start since the status line took its place). */
function TodayBlock() {
  const s = useStore();
  const { navigate } = useNav();
  const { t: text } = useLook();
  const t = todayLine(s, text);
  const ordering = orderingOpen(s);
  return (
    <section className="card today" aria-label="Today at Tikkas">
      <span className="today-ph"><img src={imageFor('rice')} alt="" /><span className="today-ov"><span className={`today-dot ${t.open ? 'open' : ''}`} /><b>{t.head}</b><span>{ordering ? t.sub : s.config?.ordering?.message ?? 'Online ordering opens soon'}</span></span></span>
      <button type="button" className="yellow today-go" onClick={() => navigate('/menu', { root: true })}>{ordering ? text('button.order_now') : 'See the menu'}</button>
    </section>
  );
}

/**
 * Your usual: the latest saved order, else the last order, as one compact row with a one-tap Reorder; with neither, the
 * newest favourite (a bowl opens the builder, a side goes straight in the bag).
 */
function UsualBlock() {
  const s = useStore();
  const { navigate } = useNav();
  const { t: text } = useLook();
  const menu = s.menuData?.menu;
  const u = s.me?.customer;
  if (!u || !menu) return null;
  const saved = s.me?.savedOrders[0];
  const last = s.me?.lastOrder;
  const lines = saved ? saved.lines : last ? last.lines.filter((l) => !l.config.reward && !l.config.membersPrice).map((l) => ({ config: l.config, qty: l.qty })) : [];
  if (!lines.length) {
    const fav = s.favourites.map((id) => menu.items[id]).find((it) => it && !it.soldOut);
    if (!fav) return null;
    const pick = () => {
      if (fav.hasTikkas || fav.sizes.length > 1) {
        s.setBuilder(newBuilder(fav.id));
        navigate('/build');
      } else {
        s.addLine({ itemId: fav.id, sizeIndex: 0, portions: [], sauces: [], toppingsOff: [], upgradeIds: [] });
        s.toast(`${fav.name} added`);
      }
    };
    return (
      <section className="usualblk" aria-label={text('home.usual')}>
        <span className="kick">Your favourite</span>
        <div className="card usualrow">
          {fav.image || fav.photoUrl ? <img src={imageFor(fav.image, fav.photoUrl)} alt="" /> : <span className="upic"><Svg html={ART[fav.id] ?? ART.tots!} /></span>}
          <span className="grow col" style={{ gap: 2, minWidth: 0 }}><b className="one">{fav.name}</b><span className="small muted one">{fav.sizes.length > 1 ? 'From ' : ''}{fmt(fav.sizes[0]!.pricePence)} · {fav.description}</span></span>
          <button type="button" className="btn2 dark reord" onClick={pick} disabled={!orderingOpen(s)}>{fav.hasTikkas || fav.sizes.length > 1 ? 'Order' : 'Add'}</button>
        </div>
      </section>
    );
  }
  const tk = lines.find((l) => menu.items[l.config.itemId]?.hasTikkas && l.config.portions.length) ?? lines[0]!;
  const it = menu.items[tk.config.itemId];
  const total = lines.reduce((a, l) => a + (safePrice(menu, { id: 'x', config: { ...l.config, reward: null }, qty: l.qty })?.linePence ?? 0), 0);
  const title = saved ? saved.name : it?.hasTikkas ? sizeTitle(menu, tk.config) : it?.name ?? 'Your last order';
  const more = lines.length > 1 ? ` + ${lines.length - 1} more` : '';
  const sub = `${it?.hasTikkas ? lineSummary(menu, tk.config) : it?.name ?? ''}${more}`;
  const reorder = () => {
    for (const l of lines) s.addLine({ ...l.config, reward: null }, l.qty);
    track('bowl_started', { from: saved ? 'saved_order' : 'last_order' });
    navigate('/bag');
  };
  return (
    <section className="usualblk" aria-label={text('home.usual')}>
      <span className="kick">{saved ? text('home.usual') : 'Last time'}</span>
      <div className="card usualrow">
        {it && (it.image || it.photoUrl) ? <img src={imageFor(it.image, it.photoUrl)} alt="" /> : <span className="upic"><Svg html={(it && ART[it.id]) ?? ART.tots!} /></span>}
        <span className="grow col" style={{ gap: 2, minWidth: 0 }}><b className="one">{title}</b><span className="small muted one">{fmt(total)} · {sub}</span></span>
        <button type="button" className="btn2 dark reord" onClick={reorder} disabled={!orderingOpen(s)} aria-label={`Reorder ${title}, ${fmt(total)}`}>{text('button.reorder')}</button>
      </div>
    </section>
  );
}

/** What's new: Saif's picture cards (photo, title, a line, a link), side by side. */
function WhatsNewBlock({ cards }: { cards: WhatsNewCard[] }) {
  const { navigate } = useNav();
  const { t } = useLook();
  if (!cards.length) return null;
  const open = (link: string | null) => {
    const to = linkTarget(link);
    if (to?.kind === 'external') openLink(to.url);
    else if (to) navigate(to.path);
  };
  return (
    <section className="col" style={{ gap: 8 }} aria-label={t('home.whats_new')}>
      <h2 className="h3">{t('home.whats_new')}</h2>
      <div className={`wnrow ${cards.length === 1 ? 'one' : ''}`}>
        {cards.map((c) => (
          <button type="button" className="card wncard" key={c.id} onClick={() => open(c.link)} disabled={!c.link}>
            {c.imageUrl ? <img src={apiUrl(c.imageUrl)} alt="" loading="lazy" /> : null}
            <span className="col" style={{ gap: 2, padding: '10px 12px 12px', minWidth: 0 }}><b className="one">{c.title}</b>{c.text ? <span className="small muted wntext">{c.text}</span> : null}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

/** Signed out: the join card where the Tikkas card would be. */
function JoinCard() {
  const s = useStore();
  const { navigate } = useNav();
  const { v } = useLook();
  const face = cardFace(v.cardDesign, v.theme.motion, logoUrlFor(v));
  const welcome = rewardPoints(s).signup;
  return (
    <section className={`${face.className} still joincard`} aria-label="Join the Rewards Club" {...face.attrs} style={face.style}>
      <CardLayers d={v.cardDesign} />
      <div className="bc-top"><CardLogo data-intro-target="" /><Svg html={COIN(26)} /></div>
      <p className="bc-pitch">Join with your mobile number. {welcome > 0 ? `${welcomeLine(welcome).charAt(0).toUpperCase()}${welcomeLine(welcome).slice(1)}.` : 'Points at the till, rewards and store credit.'}</p>
      <div className="bc-foot"><span className="bc-kick">Rewards Club</span><button type="button" className="btn2 dark" onClick={() => navigate('/join?next=/')}>Join</button></div>
    </section>
  );
}

export function Home() {
  const s = useStore();
  const { navigate, route } = useNav();
  const u = s.me?.customer ?? null;
  const [home, setHome] = useState<HomeDto>(() => keptHome() ?? DEFAULT_HOME); // the kept layout first: the card's place never changes after the first paint
  useEffect(() => {
    loadHome().then(setHome).catch(() => undefined);
  }, [u?.id]);
  const { v, t } = useLook();
  const h = new Date().getHours();
  const greet = t(h < 12 ? 'home.greeting_morning' : h < 18 ? 'home.greeting_afternoon' : 'home.greeting_evening');
  // Signed out, a guest's own orders (kept on this phone by their links) take the live row's place (docs/30).
  const live = s.me ? s.me.liveOrders : s.guestLive;
  const guestTokenOf = (id: string) => s.guestOrders.find((g) => g.id === id)?.token;
  const card = useCard(s);
  const just = useJustHappened();
  // The old Loyalty and Offers paths land here (Saif, 21 Oct evening): /card shows Home; /card?code=1 (a live order's
  // "Show my code", the Wallet pass) opens the code; /offers and /rewards open the Rewards sheet. The address becomes /
  // so Back doesn't replay it.
  const codeAsked = wantsCode(route.search);
  const topup = route.search.get('topup') === '1';
  useEffect(() => {
    const p = route.path;
    if (p === '/offers' || p === '/rewards') {
      if (u) s.openSheet({ kind: 'rewards' });
      history.replaceState(history.state, '', '/');
    } else if (p === '/card' || p === '/code') {
      if (!codeAsked && !topup) history.replaceState(history.state, '', '/');
    }
  }, [route.path, u?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!u || !codeAsked) return;
    // The deep link (a live order's push, the Wallet pass): the pop-up opens as a sheet over Home, the address becomes plain Home first.
    navigate(withoutCode('/', route.search), { replace: true, root: true });
    s.openSheet({ kind: 'code' });
  }, [u?.id, codeAsked]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (u && topup && orderingOpen(s)) {
      s.openSheet({ kind: 'topup' });
      history.replaceState(history.state, '', '/');
    }
  }, [u?.id, topup]); // eslint-disable-line react-hooks/exhaustive-deps

  // The notifications screen (Saif, 25 Oct 2026): right after joining, and again N days after a Not now; or the one-time
  // offers question on a phone that had already allowed notifications.
  useNotificationsAsk('home', s.meLoaded && route.path === '/');
  const ids = homeSectionsToDraw(home.blocks, { liveOrder: live.length > 0 });
  const section = (id: HomeBlockId) => {
    switch (id) {
      case 'greeting': return <h1 key={id} className="display hometitle">{greet}{u?.firstName ? `, ${u.firstName}` : ''}</h1>;
      case 'today': return <TodayBlock key={id} />;
      case 'card': return u ? (
        <div key={id} className="col" style={{ gap: 10 }}>
          <TikkasCard card={card} onMenu={() => s.openSheet({ kind: 'cardMenu' })} onCode={() => s.openSheet({ kind: 'code' })} />
          {just ? <p className="justnow"><Svg html={COIN(16)} /><span>{just}</span></p> : welcomeToCome(s) > 0 ? <p className="justnow muted"><Svg html={COIN(16)} /><span>{welcomeLine(welcomeToCome(s))}</span></p> : null}
        </div>
      ) : <JoinCard key={id} />;
      case 'pills': return u ? (
        <div key={id} className="actrow glass">
          {orderingOpen(s) ? <button type="button" className="actpill" onClick={() => s.openSheet({ kind: 'topup' })}><Svg html={IC.card} />{t('button.top_up')}</button> : null}
          <button type="button" className="actpill" onClick={() => s.openSheet({ kind: 'rewards' })}><Svg html={IC.gift} />Rewards</button>
          <button type="button" className="actpill" onClick={() => s.openSheet({ kind: 'earn' })}><Svg html={IC.spark} />Earn</button>
        </div>
      ) : (
        <button type="button" key={id} className="yellow" onClick={() => navigate('/menu', { root: true })}>{orderingOpen(s) ? t('button.order_now') : 'See the menu'}</button>
      );
      case 'live_order': return live.length ? <div className="col" style={{ gap: 10 }} key={id}>{live.map((o) => <LiveRow key={o.id} o={o} guestToken={u ? undefined : guestTokenOf(o.id)} />)}</div> : null;
      case 'usual': return <UsualBlock key={id} />;
      case 'whats_new': return <WhatsNewBlock key={id} cards={home.whatsNew} />;
      default: return null;
    }
  };
  return (
    <>
      <Scroll padStyle={{ gap: 16 }}>
        {/* The greeting is the page title, like Order's and Account's (Saif, 22 Oct); no logo, no status line. The account button only while Account isn't a tab (Apple). */}
        {v.accountButton && !accountIsTab(v) ? <div className="homehead"><span /><AccountButton /></div> : null}
        {ids.map(section)}
        {u && !orderingOpen(s) ? <OrderingClosed compact /> : null}
        <MessageCard />
        {testToolsAllowed(s.config?.testTools) && <p className="staging" style={{ textAlign: 'center' }}>Staging · test mode · nothing real is charged</p>}
      </Scroll>
      {/* The code's one home: the one amber object besides the card, in thumb reach above the tab bar. */}
      {u ? createPortal(<button type="button" className="showcode amberpill" onClick={() => s.openSheet({ kind: 'code' })}><Svg html={IC.qr} />Show code</button>, document.body) : null}
    </>
  );
}
